import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Search, Archive, Trash2, Star, StarOff, Mail, MailOpen,
  ArrowLeft, Paperclip, ChevronDown, Sparkles, Tag, MoreVertical,
  RefreshCw, PenLine, Copy, Send, Check, AlertTriangle, FileText,
  Inbox as InboxIcon, ArchiveRestore, Shield, Bookmark, MoreHorizontal,
  CheckSquare, Square, X, Menu, IdCard,
} from "lucide-react";
import InboxSidebar from "@/components/InboxSidebar";
import AliasCardDialog from "@/components/AliasCardDialog";
import MoveToMenu from "@/components/MoveToMenu";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
  DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent,
} from "@/components/ui/dropdown-menu";
import { formatDistanceToNow } from "date-fns";

function parseUTC(dateStr) {
  if (!dateStr) return new Date();
  let s = String(dateStr);
  s = s.replace(/[Zz]$/, "").replace(/\+00:?00$/, "");
  s = s.replace(/(\.\d{3})\d*$/, "$1");
  if (!s.endsWith("Z")) s += "Z";
  return new Date(s);
}
import EmailDetail from "@/components/EmailDetail";
import CountdownBanner from "@/components/CountdownBanner";
import ComposeEmail from "@/components/ComposeEmail";
import SendLimitBanner from "@/components/SendLimitBanner";
import UpgradeMenu from "@/components/UpgradeMenu";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "unread", label: "Unread" },
  { key: "vip", label: "Star" },
  { key: "archived", label: "Archived" },
];

const POLL_INTERVAL = 5000;

export default function InboxPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "superadmin";
  const [emails, setEmails] = useState([]);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [total, setTotal] = useState(0);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [labels, setLabels] = useState([]);
  const [activeFilters, setActiveFilters] = useState({ categoryIds: [], labelIds: [] });
  const [selectedIds, setSelectedIds] = useState(new Set());
  const bulkMode = selectedIds.size > 0;
  const [seeding, setSeeding] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [newEmailIds, setNewEmailIds] = useState(new Set());
  const [lastRefresh, setLastRefresh] = useState(null);
  const [activeAlias, setActiveAlias] = useState(null);
  // Scroll-preservation for the email-list ScrollArea across email-detail toggles.
  // Target the underlying Radix viewport via data-attribute (set by shadcn).
  const emailListScrollRef = useRef(null);
  const savedScrollTopRef = useRef(0);
  const getEmailListViewport = () =>
    emailListScrollRef.current?.querySelector("[data-radix-scroll-area-viewport]") || null;
  const [allAliases, setAllAliases] = useState([]);
  const [expired, setExpired] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composePrefill, setComposePrefill] = useState(null);
  const [sendStatus, setSendStatus] = useState({ remaining: null, total: null, limitReached: false, credits: null, maxCredits: null });
  const [aliasSummary, setAliasSummary] = useState({ total: 0, active: 0, aliases: [] });
  const [inboxAliasId, setInboxAliasId] = useState(() => {
    try { const s = JSON.parse(localStorage.getItem("inbox_sidebar_selection") || "{}"); return s.aliasId || null; } catch { return null; }
  });
  const [selectedFolder, setSelectedFolder] = useState(() => {
    try { const s = JSON.parse(localStorage.getItem("inbox_sidebar_selection") || "{}"); return s.folder || "inbox"; } catch { return "inbox"; }
  });
  const prevEmailIdsRef = useRef(new Set());
  const isVisibleRef = useRef(true);
  const countdownRef = useRef(null);

  // Fetch alias summary for inbox context header
  useEffect(() => {
    api.get("/aliases/summary").then(({ data }) => {
      setAliasSummary(data);
    }).catch(() => {});
  }, []);

  // Persist selection
  useEffect(() => {
    try { localStorage.setItem("inbox_sidebar_selection", JSON.stringify({ aliasId: inboxAliasId, folder: selectedFolder })); } catch {}
  }, [inboxAliasId, selectedFolder]);

  const handleSidebarSelect = (aliasId, folder) => {
    setInboxAliasId(aliasId);
    setSelectedFolder(folder);
    setSelectedEmail(null);
    setLoading(true);
    // Clear filters when switching alias
    if (aliasId !== inboxAliasId) {
      setActiveFilters({ categoryIds: [], labelIds: [] });
    }
  };

  /**
   * Row-badge filter handler — single-replace mode (PROMPT 007).
   *   - Clicking a badge whose id is the SOLE active filter for that bucket → clears it
   *   - Otherwise → replaces that bucket's filter with [id]
   *   - Categories and labels are independent buckets; clicking one never affects the other.
   */
  const handleBadgeFilter = (type, id) => {
    setSelectedEmail(null);
    setActiveFilters((prev) => {
      const next = { ...prev };
      if (type === "category") {
        const current = prev.categoryIds || [];
        const isSole = current.length === 1 && current[0] === id;
        next.categoryIds = isSole ? [] : [id];
      } else if (type === "label") {
        const current = prev.labelIds || [];
        const isSole = current.length === 1 && current[0] === id;
        next.labelIds = isSole ? [] : [id];
      }
      return next;
    });
  };

  // Fetch primary alias for countdown + full alias list for compose
  useEffect(() => {
    api.get("/aliases").then(({ data }) => {
      const aliases = Array.isArray(data) ? data : data.aliases || [];
      setAllAliases(aliases);
      const freeActive = aliases.find((a) => a.is_active && a.expires_at);
      if (freeActive) setActiveAlias(freeActive);
    }).catch(() => {});
  }, []);

  // Fetch send status + credits for active alias (skip for admin)
  const fetchSendStatus = useCallback(async () => {
    if (isAdmin) {
      setSendStatus({ remaining: 999, total: 999, limitReached: false, credits: null, maxCredits: null });
      return;
    }
    try {
      const { data } = await api.get("/nina/status");
      const base = {
        remaining: data.sends_remaining,
        total: data.sends_total,
        limitReached: !data.send_allowed,
      };
      if (inboxAliasId) {
        try {
          const { data: cred } = await api.get(`/aliases/${inboxAliasId}/credits`);
          base.credits = cred.credits;
          base.maxCredits = cred.max_credits;
          base.totalSent = cred.total_sent_count;
          base.maxSends = cred.max_sends;
          if (cred.credits <= 0 || cred.total_sent_count >= cred.max_sends) {
            base.limitReached = true;
          }
        } catch { /* credits endpoint may not exist for this alias */ }
      }
      setSendStatus(base);
    } catch { /* ignore */ }
  }, [inboxAliasId, isAdmin]);
  useEffect(() => { fetchSendStatus(); }, [fetchSendStatus]);

  const fetchEmails = useCallback(async (silent = false) => {
    if (!silent) setIsPolling(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (filter === "unread") params.status = "unread";
      if (filter === "vip") params.is_vip = "true";
      if (inboxAliasId) params.alias_id = inboxAliasId;
      // Use folder param for backend filtering
      if (selectedFolder && selectedFolder !== "inbox") {
        params.folder = selectedFolder;
      } else if (!filter || filter === "all") {
        params.folder = "inbox";
      }
      // Apply category/label filters
      if (activeFilters.categoryIds?.length > 0) {
        params.category_ids = activeFilters.categoryIds.join(",");
      }
      if (activeFilters.labelIds?.length > 0) {
        params.label_ids = activeFilters.labelIds.join(",");
      }
      const { data } = await api.get("/emails", { params });

      // Detect newly arrived emails
      if (silent && prevEmailIdsRef.current.size > 0) {
        const incoming = new Set();
        for (const e of data.emails) {
          if (!prevEmailIdsRef.current.has(e.email_id)) {
            incoming.add(e.email_id);
          }
        }
        if (incoming.size > 0) {
          setNewEmailIds((prev) => new Set([...prev, ...incoming]));
          toast.info(`${incoming.size} new email${incoming.size > 1 ? "s" : ""} received`, {
            duration: 3000,
          });
          countdownRef.current?.pulse();
        }
      }

      // Update previous IDs ref
      prevEmailIdsRef.current = new Set(data.emails.map((e) => e.email_id));

      setEmails(data.emails);
      setTotal(data.total);
      setLastRefresh(new Date());
    } catch {
      if (!silent) toast.error("Failed to load emails");
    } finally {
      setLoading(false);
      setIsPolling(false);
    }
  }, [search, filter, inboxAliasId, selectedFolder, activeFilters]);

  const fetchCategories = useCallback(async () => {
    try {
      const { data } = await api.get("/categories");
      setCategories(data);
    } catch { /* ignore */ }
  }, []);

  const fetchLabels = useCallback(async () => {
    try {
      const { data } = await api.get("/labels");
      setLabels(data);
    } catch { /* ignore */ }
  }, []);

  // Initial load
  useEffect(() => { fetchEmails(); }, [fetchEmails]);
  useEffect(() => { fetchCategories(); }, [fetchCategories]);
  useEffect(() => { fetchLabels(); }, [fetchLabels]);

  // ── Handle return from Stripe checkout: /inbox?upgrade=success|cancelled&type=... ──
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const handledUpgradeRef = useRef(false);
  useEffect(() => {
    if (handledUpgradeRef.current) return;
    const upgrade = searchParams.get("upgrade");
    if (!upgrade) return;
    handledUpgradeRef.current = true;
    const type = (searchParams.get("type") || "").replace(/_/g, " ");
    if (upgrade === "success") {
      toast.success("Payment successful. Your receipt will arrive in this inbox shortly.");
      // Trigger refresh using existing pipeline
      setTimeout(() => fetchEmails(true), 800);
      setTimeout(() => fetchEmails(true), 4000);
    } else if (upgrade === "cancelled") {
      toast(`Checkout cancelled${type ? ` (${type})` : ""}. No changes made.`);
    }
    // Clean the URL so refresh doesn't replay
    const next = new URLSearchParams(searchParams);
    next.delete("upgrade");
    next.delete("type");
    next.delete("session_id");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, fetchEmails]);


  // 5-second polling — pauses when tab is hidden
  useEffect(() => {
    const intervalId = setInterval(() => {
      if (isVisibleRef.current) {
        fetchEmails(true);
      }
    }, POLL_INTERVAL);

    const handleVisibility = () => {
      isVisibleRef.current = !document.hidden;
      if (!document.hidden) {
        fetchEmails(true);
        countdownRef.current?.pulse();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [fetchEmails]);

  const handleAction = async (emailId, action, categoryId, labelId) => {
    try {
      const payload = { action };
      if (categoryId) payload.category_id = categoryId;
      if (labelId) payload.label_id = labelId;
      await api.post(`/emails/${emailId}/actions`, payload);

      if (action === "delete" || action === "archive" || action === "spam" || action === "not_spam" || action === "unarchive") {
        setEmails((prev) => prev.filter((e) => e.email_id !== emailId));
        if (selectedEmail?.email_id === emailId) setSelectedEmail(null);
      } else if (action === "add_category" || action === "remove_category") {
        // Optimistic update for categories
        setEmails((prev) => prev.map((e) => {
          if (e.email_id !== emailId) return e;
          const ids = e.category_ids || [];
          return {
            ...e,
            category_ids: action === "add_category"
              ? [...new Set([...ids, categoryId])]
              : ids.filter(id => id !== categoryId),
          };
        }));
        if (selectedEmail?.email_id === emailId) {
          setSelectedEmail((prev) => {
            const ids = prev?.category_ids || [];
            return {
              ...prev,
              category_ids: action === "add_category"
                ? [...new Set([...ids, categoryId])]
                : ids.filter(id => id !== categoryId),
            };
          });
        }
        fetchCategories(); // refresh sort order
      } else if (action === "add_label" || action === "remove_label") {
        // Optimistic update for labels
        setEmails((prev) => prev.map((e) => {
          if (e.email_id !== emailId) return e;
          const ids = e.label_ids || [];
          return {
            ...e,
            label_ids: action === "add_label"
              ? [...new Set([...ids, labelId])]
              : ids.filter(id => id !== labelId),
          };
        }));
        if (selectedEmail?.email_id === emailId) {
          setSelectedEmail((prev) => {
            const ids = prev?.label_ids || [];
            return {
              ...prev,
              label_ids: action === "add_label"
                ? [...new Set([...ids, labelId])]
                : ids.filter(id => id !== labelId),
            };
          });
        }
        fetchLabels(); // refresh sort order
      } else if (action === "vip" || action === "unvip") {
        // Optimistic update for star/VIP toggle
        const newVipState = action === "vip";
        setEmails((prev) => prev.map((e) => 
          e.email_id === emailId ? { ...e, is_vip: newVipState } : e
        ));
        if (selectedEmail?.email_id === emailId) {
          setSelectedEmail((prev) => ({ ...prev, is_vip: newVipState }));
        }
      } else {
        fetchEmails();
      }
    } catch {
      toast.error("Action failed");
    }
  };

  const handleCreateCategory = async (name) => {
    try {
      await api.post("/categories", { name });
      fetchCategories();
      toast.success(`Category "${name}" created`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create category");
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    try {
      await api.delete(`/categories/${categoryId}`);
      fetchCategories();
      // Remove from local email state
      setEmails((prev) => prev.map(e => ({
        ...e,
        category_ids: (e.category_ids || []).filter(id => id !== categoryId),
      })));
      toast.success("Category deleted");
    } catch {
      toast.error("Failed to delete category");
    }
  };

  const handleCreateLabel = async (name) => {
    try {
      await api.post("/labels", { name });
      fetchLabels();
      toast.success(`Label "${name}" created`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create label");
    }
  };

  const handleDeleteLabel = async (labelId) => {
    try {
      await api.delete(`/labels/${labelId}`);
      fetchLabels();
      setEmails((prev) => prev.map(e => ({
        ...e,
        label_ids: (e.label_ids || []).filter(id => id !== labelId),
      })));
      toast.success("Label deleted");
    } catch {
      toast.error("Failed to delete label");
    }
  };

  // Bulk selection helpers
  const [selectAllMatching, setSelectAllMatching] = useState(false);

  const toggleSelectEmail = (emailId) => {
    setSelectAllMatching(false);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(emailId)) next.delete(emailId);
      else next.add(emailId);
      return next;
    });
  };

  const selectAll = () => {
    setSelectedIds(new Set(emails.map(e => e.email_id)));
    setSelectAllMatching(false);
  };

  const selectAllAcrossResults = () => {
    setSelectAllMatching(true);
    setSelectedIds(new Set(emails.map(e => e.email_id)));
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
    setSelectAllMatching(false);
  };

  const bulkCount = selectAllMatching ? total : selectedIds.size;

  const handleBulkAction = async (action, categoryId, labelId) => {
    if (bulkCount === 0) return;
    try {
      const payload = { action };
      if (categoryId) payload.category_id = categoryId;
      if (labelId) payload.label_id = labelId;

      if (selectAllMatching) {
        // Server-side resolution of matching emails
        payload.select_all_matching = true;
        payload.filter_folder = selectedFolder || "inbox";
        if (inboxAliasId) payload.filter_alias_id = inboxAliasId;
        if (activeFilters.categoryIds?.length > 0) payload.filter_category_ids = activeFilters.categoryIds.join(",");
        if (activeFilters.labelIds?.length > 0) payload.filter_label_ids = activeFilters.labelIds.join(",");
        if (filter === "unread") payload.filter_status = "unread";
        if (filter === "vip") payload.filter_is_vip = "true";
      } else {
        payload.email_ids = [...selectedIds];
      }

      const { data } = await api.post("/emails/batch-actions", payload);
      const count = data.count || bulkCount;

      if (action === "delete" || action === "archive" || action === "spam" || action === "not_spam" || action === "unarchive") {
        if (selectAllMatching) {
          setEmails([]);
          setTotal(0);
        } else {
          setEmails((prev) => prev.filter(e => !selectedIds.has(e.email_id)));
        }
        if (selectedEmail && selectedIds.has(selectedEmail.email_id)) setSelectedEmail(null);
      } else {
        fetchEmails();
      }
      clearSelection();

      // Undo toast for destructive/move actions
      if (data.undo_id && ["archive", "delete", "spam", "not_spam"].includes(action)) {
        const actionLabel = action === "spam" ? "marked as spam" : action === "delete" ? "deleted" : "archived";
        toast(`${count} email${count !== 1 ? "s" : ""} ${actionLabel}`, {
          action: {
            label: "Undo",
            onClick: async () => {
              try {
                await api.post(`/emails/undo/${data.undo_id}`);
                toast.success("Action undone");
                fetchEmails();
              } catch {
                toast.error("Undo failed — window may have expired");
              }
            },
          },
          duration: 8000,
        });
      } else {
        toast.success(`${action.replace("_", " ")} applied to ${count} email${count !== 1 ? "s" : ""}`);
      }
    } catch {
      toast.error("Bulk action failed");
    }
  };

  const handleSeedDemo = async () => {
    setSeeding(true);
    try {
      await api.post("/emails/seed-demo");
      toast.success("Demo emails created");
      fetchEmails();
    } catch {
      toast.error("Failed to seed demo data");
    } finally {
      setSeeding(false);
    }
  };

  const handleSelectEmail = async (email) => {
    // Preserve current scroll position of the email list so we can restore
    // it when the user returns (either via Back button or the My Inbox
    // bottom-switch button on mobile).
    const vp = getEmailListViewport();
    if (vp) savedScrollTopRef.current = vp.scrollTop;
    setSelectedEmail(email);
    // Clear "new" highlight when selecting
    setNewEmailIds((prev) => {
      const next = new Set(prev);
      next.delete(email.email_id);
      return next;
    });
    // Always fetch full email data (for body_html)
    try {
      const { data } = await api.get(`/emails/${email.email_id}`);
      setEmails((prev) => prev.map((e) => e.email_id === email.email_id ? { ...e, ...data } : e));
      setSelectedEmail((prev) => ({ ...prev, ...data }));
    } catch { /* ignore */ }
  };

  // Restore scroll position whenever we return to the list view.
  useEffect(() => {
    if (selectedEmail) return;
    const vp = getEmailListViewport();
    if (vp && savedScrollTopRef.current > 0) {
      // Defer to next frame so the list has re-rendered first.
      requestAnimationFrame(() => { vp.scrollTop = savedScrollTopRef.current; });
    }
  }, [selectedEmail]);

  // Listen for the "go home to inbox" event dispatched by the global
  // MobileAdminSwitch. When the user taps "My Inbox" while already on
  // /inbox (e.g. from within the email-detail overlay on mobile), the
  // route doesn't change — so React Router won't remount. We handle it
  // here by closing the detail overlay and restoring the saved scroll.
  useEffect(() => {
    const onHome = () => setSelectedEmail(null);
    window.addEventListener("quickinbox:inbox-home", onHome);
    return () => window.removeEventListener("quickinbox:inbox-home", onHome);
  }, []);

  const unreadCount = emails.filter((e) => e.status === "unread").length;

  const handleSent = (data) => {
    fetchSendStatus();
    if (data?.limit_reached) return;
    fetchEmails(true);
    countdownRef.current?.pulse();
  };

  // Impersonation detection
  const isImpersonating = typeof window !== "undefined" && sessionStorage.getItem("impersonate_active") === "true";

  // Compose alias: use sidebar-selected alias, or first active alias from full list
  const composeAlias = (() => {
    if (inboxAliasId) {
      return allAliases.find(a => a.alias_id === inboxAliasId) || null;
    }
    return allAliases.find(a => a.is_active) || allAliases[0] || null;
  })();

  const canSend = isAdmin ? (composeAlias || activeAlias) : (!expired && !sendStatus.limitReached && (composeAlias || activeAlias));

  const handleReply = (email) => {
    // For outbound (sent) mail: reply to the recipient, not sender
    const replyTo = email.direction === "outbound"
      ? (email.to_email || "")
      : (email.sender_email || email.sender_name || "");

    // Prevent stacking Re: Re: Re:
    const subj = email.subject || "";
    const replySubject = /^re:/i.test(subj) ? subj : `Re: ${subj}`;

    const senderLabel = email.direction === "outbound"
      ? (email.to_email || "?")
      : (email.sender_name || email.sender_email || "?");

    setComposePrefill({
      mode: "reply",
      to: replyTo,
      subject: replySubject,
      body: `\n\n---\nOn ${email.created_at ? new Date(email.created_at).toLocaleString() : "?"}, ${senderLabel} wrote:\n> ${(email.body || email.body_preview || "").split("\n").join("\n> ")}`,
      aliasId: email.alias_id || inboxAliasId,
    });
    setComposeOpen(true);
  };

  const handleForward = (email) => {
    // Prevent stacking Fwd: Fwd: Fwd:
    const subj = email.subject || "";
    const fwdSubject = /^fwd:/i.test(subj) ? subj : `Fwd: ${subj}`;

    setComposePrefill({
      mode: "forward",
      to: "",
      subject: fwdSubject,
      body: `\n\n---\nForwarded message:\nFrom: ${email.sender_name || ""} <${email.sender_email || ""}>\nDate: ${email.created_at ? new Date(email.created_at).toLocaleString() : "?"}\nSubject: ${email.subject || ""}\n\n${email.body || email.body_preview || ""}`,
      aliasId: email.alias_id || inboxAliasId,
      hasAttachments: email.has_attachments || false,
    });
    setComposeOpen(true);
  };

  return (
    <div className="h-full flex flex-col relative page-enter" data-testid="inbox-page">
      {/* Countdown Banner — free users only */}
      {!isAdmin && activeAlias?.expires_at && (
        <CountdownBanner
          ref={countdownRef}
          expiresAt={activeAlias.expires_at}
          createdAt={activeAlias.created_at}
          onExpired={() => setExpired(true)}
        />
      )}

      {/* Send Limit Banner — free users only */}
      {!isAdmin && (
        <SendLimitBanner
          remaining={sendStatus.remaining}
          total={sendStatus.total}
          limitReached={sendStatus.limitReached}
        />
      )}

      {/* Expired overlay — free users only */}
      {!isAdmin && expired && (
        <div className="absolute inset-0 z-20 bg-white/80 backdrop-blur-sm flex items-center justify-center" data-testid="expired-overlay">
          {/* Overlay blocks interaction — CTA is in the banner above */}
        </div>
      )}

      {/* Compose Dialog */}
      {(composeAlias || activeAlias) && (
        <ComposeEmail
          open={composeOpen}
          onOpenChange={(open) => { setComposeOpen(open); if (!open) setComposePrefill(null); }}
          aliasId={(composeAlias || activeAlias).alias_id}
          aliasAddress={(composeAlias || activeAlias).alias_address}
          aliases={allAliases}
          onSent={handleSent}
          sendsRemaining={(isImpersonating || isAdmin) ? null : sendStatus.remaining}
          sendsTotal={(isImpersonating || isAdmin) ? null : sendStatus.total}
          isImpersonating={isImpersonating}
          prefill={composePrefill}
        />
      )}

      <div className="flex-1 flex min-h-0">
      {/* Desktop Sidebar */}
      <div className="hidden xl:block w-[220px] xl:w-[240px] shrink-0">
        <InboxSidebar
          aliases={aliasSummary.aliases}
          totalAliases={aliasSummary.total}
          selectedAliasId={inboxAliasId}
          selectedFolder={selectedFolder}
          onSelect={handleSidebarSelect}
          totalUnread={emails.filter((e) => e.status === "unread").length}
          categories={categories}
          labels={labels}
          activeFilters={activeFilters}
          onFilterChange={setActiveFilters}
        />
      </div>

      {/* Mobile drawer */}
      <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
        <SheetContent
          side="left"
          className="w-72 p-0 xl:hidden overflow-y-auto"
          data-testid="inbox-sidebar-drawer"
          style={{
            paddingTop: "env(safe-area-inset-top)",
            paddingBottom: "env(safe-area-inset-bottom)",
          }}
        >
          <InboxSidebar
            aliases={aliasSummary.aliases}
            totalAliases={aliasSummary.total}
            selectedAliasId={inboxAliasId}
            selectedFolder={selectedFolder}
            onSelect={(...args) => { setMobileNavOpen(false); handleSidebarSelect(...args); }}
            totalUnread={emails.filter((e) => e.status === "unread").length}
            categories={categories}
            labels={labels}
            activeFilters={activeFilters}
            onFilterChange={setActiveFilters}
          />
        </SheetContent>
      </Sheet>

      {/* Email List Panel */}
      <div className={`${selectedEmail ? "hidden xl:flex" : "flex"} w-full xl:w-[380px] xl:border-r border-[#E5E5E5] flex-col bg-white`}>
        {/* Admin Inbox indicator — DESKTOP-ONLY explicit return path to /housekeeping.
            On mobile, the fixed MobileAdminSwitch at the bottom handles context
            switching with a consistent, always-visible control. */}
        {isAdmin && (
          <button
            type="button"
            onClick={() => navigate("/housekeeping")}
            className="hidden xl:flex w-full px-4 py-2 bg-zinc-900 hover:bg-zinc-800 items-center gap-2 text-left transition-colors"
            data-testid="admin-inbox-indicator"
            aria-label="Return to Admin panel"
          >
            <ArrowLeft size={12} className="text-zinc-400" />
            <Shield size={12} className="text-zinc-400" />
            <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">Admin Inbox</span>
            <span className="text-[10px] text-zinc-300 ml-auto underline-offset-2 hover:underline" data-testid="admin-inbox-return-link">
              Back to Admin panel
            </span>
          </button>
        )}
        {/* Header */}
        <div className="px-4 pt-4 pb-3 space-y-3 border-b border-[#E5E5E5]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="xl:hidden h-9 w-9 p-0 text-[#555555]"
                onClick={() => setMobileNavOpen(true)}
                data-testid="inbox-mobile-menu-toggle"
                aria-label="Open inbox menu"
              >
                <Menu size={18} />
              </Button>
              <h2 className="font-heading text-lg font-medium text-[#111111]" data-testid="inbox-title">
                {selectedFolder.charAt(0).toUpperCase() + selectedFolder.slice(1)}
              </h2>
              {unreadCount > 0 && (
                <Badge variant="secondary" className="bg-violet-50 text-violet-600 text-[11px] font-mono" data-testid="unread-count-badge">{unreadCount}</Badge>
              )}
              {isPolling && (
                <RefreshCw size={14} className="text-[#888888] animate-spin" data-testid="polling-indicator" />
              )}
            </div>
            <div className="flex items-center gap-2">
              <div className="relative flex items-center">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" title="Auto-refresh active" data-testid="live-indicator" />
                <span className="hidden sm:inline text-[10px] text-[#888888] ml-1.5 font-mono font-medium">LIVE</span>
              </div>
              {/* Manual refresh — restores a one-tap reload action that is
                  always reachable in the inbox header on mobile and desktop. */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => fetchEmails(false)}
                disabled={isPolling}
                className="text-[#888888] hover:text-[#111111] h-9 w-9 p-0"
                title="Refresh inbox"
                aria-label="Refresh inbox"
                data-testid="inbox-refresh-button"
              >
                <RefreshCw size={15} className={isPolling ? "animate-spin" : ""} />
              </Button>
              {!isAdmin && (
                <UpgradeMenu activeAliasEmail={activeAlias?.alias_address || null} />
              )}
              <Button variant="ghost" size="sm" onClick={handleSeedDemo} disabled={seeding} className="text-[#888888] hover:text-[#111111] text-xs" data-testid="seed-demo-button" title="Load demo data">
                <Sparkles size={15} className="sm:mr-1" /> <span className="hidden sm:inline">{seeding ? "..." : "Demo"}</span>
              </Button>
            </div>
          </div>

          {/* Alias context: label + address with copy */}
          <AliasContextHeader
            aliasId={inboxAliasId}
            aliases={aliasSummary.aliases}
            aliasSummary={aliasSummary}
          />

          {/* Compose + remaining sends — full-width primary CTA on mobile, inline on desktop */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <Button
              size="sm"
              onClick={() => setComposeOpen(true)}
              disabled={!canSend}
              className="bg-violet-600 text-white hover:bg-violet-700 text-sm w-full sm:w-auto sm:text-xs h-10 sm:h-9"
              data-testid="compose-button"
            >
              <PenLine size={15} className="mr-1.5" />
              New email
            </Button>
            {!isAdmin && sendStatus.credits !== null && sendStatus.credits !== undefined && (
              <div className="flex items-center gap-3" data-testid="credit-status">
                <span className="text-[10px] text-[#888888] font-mono font-medium" data-testid="credits-counter">
                  Credits: {sendStatus.credits}
                </span>
                <span className="text-[10px] text-[#888888] font-mono font-medium" data-testid="sends-counter">
                  Sent: {sendStatus.totalSent ?? 0}/{sendStatus.maxSends ?? 10}
                </span>
              </div>
            )}
            {!isAdmin && sendStatus.credits === null && sendStatus.remaining !== null && !sendStatus.limitReached && (
              <span className="text-[10px] text-[#888888] font-mono font-medium" data-testid="sends-remaining-counter">
                {sendStatus.remaining}/{sendStatus.total} Sends
              </span>
            )}
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#888888]" size={16} />
            <Input
              placeholder="Search emails..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-9 text-sm bg-white border-[#E5E5E5]"
              data-testid="inbox-search-input"
            />
          </div>
          {/* Filter tabs / Bulk action bar */}
          {bulkMode ? (
            <div className="bg-white rounded-lg px-4 py-2.5 space-y-2" data-testid="bulk-action-bar">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-medium text-[#111111]" data-testid="bulk-selected-count">
                  {selectAllMatching ? `All ${total} emails selected` : `${selectedIds.size} selected`}
                </span>
                {!selectAllMatching && (
                  <button onClick={selectAll} className="text-[11px] text-violet-600 hover:text-violet-600 font-medium" data-testid="bulk-select-all">
                    Select all
                  </button>
                )}
                {!selectAllMatching && selectedIds.size === emails.length && total > emails.length && (
                  <button onClick={selectAllAcrossResults} className="text-[11px] text-violet-600 hover:text-violet-600 font-medium" data-testid="bulk-select-all-matching">
                    Select all {total} emails
                  </button>
                )}
                <button onClick={clearSelection} className="text-[11px] text-[#888888] hover:text-[#555555]" data-testid="bulk-clear">
                  Clear
                </button>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {/* Unified Move to… (replaces folder-aware Archive / Spam / NotSpam / Move-to-Inbox buttons) */}
                {selectedFolder !== "sent" && (
                  <MoveToMenu
                    folder={selectedFolder}
                    onAction={handleBulkAction}
                    variant="button"
                    testIdPrefix="bulk-move-to"
                  />
                )}
                <Button size="sm" variant="outline" className="h-7 text-xs gap-1 text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleBulkAction("delete")} data-testid="bulk-delete">
                  <Trash2 size={13} /> Delete
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm" variant="outline" className="h-7 text-xs gap-1" data-testid="bulk-more-trigger">
                      <MoreHorizontal size={13} /> More
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-48">
                    {(() => {
                      const isSingle = selectedIds.size === 1 && !selectAllMatching;
                      const singleEmail = isSingle ? emails.find(e => selectedIds.has(e.email_id)) : null;

                      return <>
                        {/* Read/Unread — context-aware for single, both for bulk */}
                        {isSingle && singleEmail ? (
                          singleEmail.status === "unread" ? (
                            <DropdownMenuItem onClick={() => handleBulkAction("read")} data-testid="bulk-mark-read">
                              <MailOpen size={14} className="mr-2" /> Mark as read
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem onClick={() => handleBulkAction("unread")} data-testid="bulk-mark-unread">
                              <Mail size={14} className="mr-2" /> Mark as unread
                            </DropdownMenuItem>
                          )
                        ) : (
                          <>
                            <DropdownMenuItem onClick={() => handleBulkAction("read")} data-testid="bulk-mark-read">
                              <MailOpen size={14} className="mr-2" /> Mark as read
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleBulkAction("unread")} data-testid="bulk-mark-unread">
                              <Mail size={14} className="mr-2" /> Mark as unread
                            </DropdownMenuItem>
                          </>
                        )}
                        <DropdownMenuSeparator />
                        {/* Star — context-aware for single, both for bulk */}
                        {isSingle && singleEmail ? (
                          singleEmail.is_vip ? (
                            <DropdownMenuItem onClick={() => handleBulkAction("unvip")} data-testid="bulk-remove-star">
                              <StarOff size={14} className="mr-2" /> Remove star
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem onClick={() => handleBulkAction("vip")} data-testid="bulk-add-star">
                              <Star size={14} className="mr-2" /> Add star
                            </DropdownMenuItem>
                          )
                        ) : (
                          <>
                            <DropdownMenuItem onClick={() => handleBulkAction("vip")} data-testid="bulk-add-star">
                              <Star size={14} className="mr-2" /> Add star
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleBulkAction("unvip")} data-testid="bulk-remove-star">
                              <StarOff size={14} className="mr-2" /> Remove star
                            </DropdownMenuItem>
                          </>
                        )}
                      </>;
                    })()}
                    <DropdownMenuSeparator />
                    {categories.length > 0 && (
                      <DropdownMenuSub>
                        <DropdownMenuSubTrigger><Tag size={14} className="mr-2" /> Apply category</DropdownMenuSubTrigger>
                        <DropdownMenuSubContent>
                          {categories.map((cat) => (
                            <DropdownMenuItem key={cat.category_id} onClick={() => handleBulkAction("add_category", cat.category_id)} data-testid={`bulk-cat-${cat.category_id}`}>
                              <div className="w-2 h-2 rounded-full mr-2 shrink-0" style={{ backgroundColor: cat.color }} />
                              {cat.name}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuSubContent>
                      </DropdownMenuSub>
                    )}
                    {categories.length > 0 && (
                      <DropdownMenuSub>
                        <DropdownMenuSubTrigger><Tag size={14} className="mr-2" /> Remove category</DropdownMenuSubTrigger>
                        <DropdownMenuSubContent>
                          {categories.map((cat) => (
                            <DropdownMenuItem key={cat.category_id} onClick={() => handleBulkAction("remove_category", cat.category_id)} data-testid={`bulk-cat-remove-${cat.category_id}`}>
                              <div className="w-2 h-2 rounded-full mr-2 shrink-0" style={{ backgroundColor: cat.color }} />
                              {cat.name}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuSubContent>
                      </DropdownMenuSub>
                    )}
                    {labels.length > 0 && (
                      <DropdownMenuSub>
                        <DropdownMenuSubTrigger><Bookmark size={14} className="mr-2" /> Add label</DropdownMenuSubTrigger>
                        <DropdownMenuSubContent>
                          {labels.map((lbl) => (
                            <DropdownMenuItem key={lbl.label_id} onClick={() => handleBulkAction("add_label", null, lbl.label_id)} data-testid={`bulk-lbl-${lbl.label_id}`}>
                              {lbl.name}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuSubContent>
                      </DropdownMenuSub>
                    )}
                    {labels.length > 0 && (
                      <DropdownMenuSub>
                        <DropdownMenuSubTrigger><Bookmark size={14} className="mr-2" /> Remove label</DropdownMenuSubTrigger>
                        <DropdownMenuSubContent>
                          {labels.map((lbl) => (
                            <DropdownMenuItem key={lbl.label_id} onClick={() => handleBulkAction("remove_label", null, lbl.label_id)} data-testid={`bulk-lbl-remove-${lbl.label_id}`}>
                              {lbl.name}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuSubContent>
                      </DropdownMenuSub>
                    )}
                    <DropdownMenuSeparator />
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          ) : (
            <div className="hidden xl:flex gap-1" data-testid="filter-tabs-row">
              {FILTERS.map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => setFilter(key)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                    filter === key ? "bg-zinc-900 text-white" : "text-[#555555] hover:bg-[#F5F5F5]"
                  }`}
                  data-testid={`filter-${key}`}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Email List — filter-tabs get re-rendered sticky on mobile for easy one-thumb access
            (desktop already keeps the filter row visible above the list via the static header). */}
        <ScrollArea className="flex-1" ref={emailListScrollRef}>
          {!bulkMode && (
            <div
              className="xl:hidden sticky top-0 z-10 bg-white border-b border-[#E5E5E5] px-4 py-2 flex gap-1"
              data-testid="filter-tabs-sticky"
            >
              {FILTERS.map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => setFilter(key)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                    filter === key ? "bg-zinc-900 text-white" : "text-[#555555] hover:bg-[#F5F5F5]"
                  }`}
                  data-testid={`filter-sticky-${key}`}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : emails.length === 0 ? (
            <EmptyFolderState
              folder={selectedFolder}
              aliasAddress={composeAlias?.alias_address || activeAlias?.alias_address}
              onCompose={() => setComposeOpen(true)}
              onSeedDemo={handleSeedDemo}
              seeding={seeding}
              canSend={canSend}
            />
          ) : (
            <div className="pb-16 md:pb-0">
              {emails.map((email) => (
                <EmailRow
                  key={email.email_id}
                  email={email}
                  isSelected={selectedEmail?.email_id === email.email_id}
                  isNew={newEmailIds.has(email.email_id)}
                  onClick={() => handleSelectEmail(email)}
                  onAction={handleAction}
                  categories={categories}
                  labels={labels}
                  showAliasBadge={!inboxAliasId}
                  folder={selectedFolder}
                  isChecked={selectedIds.has(email.email_id)}
                  onToggleCheck={toggleSelectEmail}
                  activeFilters={activeFilters}
                  onBadgeFilter={handleBadgeFilter}
                />
              ))}
              <div className="py-4 text-center text-xs text-[#888888] font-mono">
                {total} email{total !== 1 ? "s" : ""}
              </div>
            </div>
          )}
        </ScrollArea>
      </div>

      {/* Desktop Detail Panel */}
      <div className="hidden xl:flex flex-1 bg-white" data-testid="desktop-detail-panel">
        {selectedEmail ? (
          <EmailDetail
            email={selectedEmail}
            onAction={handleAction}
            categories={categories}
            labels={labels}
            onClose={() => setSelectedEmail(null)}
            folder={selectedFolder}
            onReply={canSend ? handleReply : null}
            onForward={canSend ? handleForward : null}
            onCreateCategory={handleCreateCategory}
            onDeleteCategory={handleDeleteCategory}
            onCreateLabel={handleCreateLabel}
            onDeleteLabel={handleDeleteLabel}
          />
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center space-y-2">
              <div className="w-14 h-14 mx-auto rounded-xl bg-[#F5F5F5] flex items-center justify-center">
                <MailOpen size={24} className="text-[#888888]" strokeWidth={1.5} />
              </div>
              <p className="text-sm text-[#888888] font-medium">Select an email to read</p>
              <p className="text-xs text-[#888888]">{emails.length} email{emails.length !== 1 ? "s" : ""} in this folder</p>
            </div>
          </div>
        )}
      </div>

      {/* Mobile Detail Overlay */}
      <AnimatePresence>
        {selectedEmail && (
          <motion.div
            className="xl:hidden fixed inset-0 bg-white z-40"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            data-testid="mobile-detail-overlay"
          >
            <div className="h-full flex flex-col">
              <div className="flex items-center gap-2 px-3 py-2.5 border-b border-[#E5E5E5]">
                <Button variant="ghost" size="sm" onClick={() => setSelectedEmail(null)} className="h-9 w-9 p-0 shrink-0" data-testid="mobile-back-button" aria-label="Back to inbox">
                  <ArrowLeft size={18} />
                </Button>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-[#111111] truncate" data-testid="mobile-detail-subject">
                    {selectedEmail.subject || "(no subject)"}
                  </p>
                  <p className="text-[10px] text-[#888888] truncate">{selectedEmail.sender_name || selectedEmail.sender_email}</p>
                </div>
              </div>
              <div className="flex-1 overflow-auto">
                <EmailDetail
                  email={selectedEmail}
                  onAction={handleAction}
                  categories={categories}
                  labels={labels}
                  onClose={() => setSelectedEmail(null)}
                  isMobile
                  folder={selectedFolder}
                  onReply={canSend ? handleReply : null}
                  onForward={canSend ? handleForward : null}
                  onCreateCategory={handleCreateCategory}
                  onDeleteCategory={handleDeleteCategory}
                  onCreateLabel={handleCreateLabel}
                  onDeleteLabel={handleDeleteLabel}
                />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      </div>{/* end flex-1 wrapper */}
    </div>
  );
}


/* Context-aware empty folder states */
function EmptyFolderState({ folder, aliasAddress, onCompose, onSeedDemo, seeding, canSend }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (!aliasAddress) return;
    navigator.clipboard.writeText(aliasAddress).then(() => {
      setCopied(true);
      toast.success("Address copied");
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => toast.error("Copy failed"));
  };

  // Sent folder
  if (folder === "sent") {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-6 text-center" data-testid="empty-inbox">
        <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center mb-4">
          <Send size={22} className="text-blue-500" strokeWidth={1.5} />
        </div>
        <h3 className="font-heading text-base font-medium text-[#111111] mb-1">No sent emails</h3>
        <p className="text-sm text-[#888888] mb-5">Emails you send will appear here.</p>
        {canSend && (
          <Button size="sm" onClick={onCompose} className="bg-violet-600 text-white hover:bg-violet-700" data-testid="empty-compose-button">
            <PenLine size={14} className="mr-1.5" /> Compose
          </Button>
        )}
      </div>
    );
  }

  // Trash
  if (folder === "trash") {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-6 text-center" data-testid="empty-inbox">
        <div className="w-12 h-12 rounded-xl bg-[#F5F5F5] flex items-center justify-center mb-4">
          <Trash2 size={22} className="text-[#888888]" strokeWidth={1.5} />
        </div>
        <h3 className="font-heading text-base font-medium text-[#111111] mb-1">Trash is empty</h3>
        <p className="text-sm text-[#888888]">Deleted emails will appear here.</p>
      </div>
    );
  }

  // Archive
  if (folder === "archive") {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-6 text-center" data-testid="empty-inbox">
        <div className="w-12 h-12 rounded-xl bg-[#F5F5F5] flex items-center justify-center mb-4">
          <Archive size={22} className="text-[#888888]" strokeWidth={1.5} />
        </div>
        <h3 className="font-heading text-base font-medium text-[#111111] mb-1">No archived emails</h3>
        <p className="text-sm text-[#888888]">Swipe or click archive to move emails here.</p>
      </div>
    );
  }

  // Spam
  if (folder === "spam") {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-6 text-center" data-testid="empty-inbox">
        <div className="w-12 h-12 rounded-xl bg-[#F5F5F5] flex items-center justify-center mb-4">
          <AlertTriangle size={22} className="text-[#888888]" strokeWidth={1.5} />
        </div>
        <h3 className="font-heading text-base font-medium text-[#111111] mb-1">No spam</h3>
        <p className="text-sm text-[#888888]">All clear.</p>
      </div>
    );
  }

  // Drafts
  if (folder === "drafts") {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-6 text-center" data-testid="empty-inbox">
        <div className="w-12 h-12 rounded-xl bg-[#F5F5F5] flex items-center justify-center mb-4">
          <FileText size={22} className="text-[#888888]" strokeWidth={1.5} />
        </div>
        <h3 className="font-heading text-base font-medium text-[#111111] mb-1">No drafts</h3>
        <p className="text-sm text-[#888888]">Saved drafts will appear here.</p>
      </div>
    );
  }

  // Inbox (default) — action-driven first-use experience
  return (
    <div className="flex flex-col items-center justify-center py-12 px-6 text-center" data-testid="empty-inbox">
      {aliasAddress ? (
        <>
          {/* Primary action: copy address */}
          <h3 className="font-heading text-base font-medium text-[#111111] mb-1">Your inbox is empty</h3>
          <p className="text-sm text-[#888888] mb-5">Share this address to start receiving emails.</p>

          <button
            onClick={handleCopy}
            className="group flex items-center gap-2.5 px-4 py-2.5 rounded-lg border border-[#E5E5E5] bg-white hover:border-[#E5E5E5] hover:bg-[#F5F5F5] transition-all mb-4 max-w-full"
            data-testid="empty-copy-address"
          >
            <span className="text-sm text-[#555555] truncate">{aliasAddress}</span>
            {copied
              ? <Check size={15} className="text-emerald-500 shrink-0" />
              : <Copy size={15} className="text-[#888888] group-hover:text-[#555555] shrink-0" />
            }
          </button>

          {/* Secondary actions */}
          <div className="flex items-center gap-3">
            {canSend && (
              <Button size="sm" variant="outline" onClick={onCompose} className="text-[#555555]" data-testid="empty-compose-button">
                <PenLine size={14} className="mr-1.5" /> Send test email
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={onSeedDemo} disabled={seeding} className="text-[#888888]" data-testid="empty-seed-button">
              <Sparkles size={14} className="mr-1.5" /> {seeding ? "Loading..." : "Load demo data"}
            </Button>
          </div>
        </>
      ) : (
        <>
          {/* No alias yet — prompt creation */}
          <div className="w-12 h-12 rounded-xl bg-[#F5F5F5] flex items-center justify-center mb-4">
            <Mail size={22} className="text-[#888888]" strokeWidth={1.5} />
          </div>
          <h3 className="font-heading text-base font-medium text-[#111111] mb-1">No emails yet</h3>
          <p className="text-sm text-[#888888] mb-5">Create an alias to get started.</p>
          <Button size="sm" onClick={onSeedDemo} disabled={seeding} className="bg-violet-600 text-white hover:bg-violet-700" data-testid="empty-seed-button">
            <Sparkles size={14} className="mr-1.5" /> {seeding ? "Loading..." : "Generate demo emails"}
          </Button>
        </>
      )}
    </div>
  );
}


/* Alias context header: label + address with copy */
function AliasContextHeader({ aliasId, aliases, aliasSummary }) {
  const [copied, setCopied] = useState(false);
  const [cardOpen, setCardOpen] = useState(false);
  const alias = aliasId ? aliases.find(a => a.alias_id === aliasId) : null;

  const handleCopy = async () => {
    if (!alias?.alias_address) return;
    try {
      await navigator.clipboard.writeText(alias.alias_address);
      setCopied(true);
      toast.success("E-Mail-Adresse kopiert");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Clipboard unavailable");
    }
  };

  if (!alias) {
    return (
      <div className="flex items-center gap-2 text-[11px] text-[#888888]" data-testid="inbox-context">
        <span>{aliasSummary.active} active / {aliasSummary.total} total aliases</span>
      </div>
    );
  }

  return (
    <div data-testid="inbox-context" className="flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium text-[#555555] tracking-wide uppercase truncate" data-testid="inbox-alias-label">
          {alias.label || alias.alias_address.split("@")[0]}
        </p>
        {/* Entire row is the copy target. Clicking anywhere on the row (text or icon)
            copies the full address. Tooltip flips from "Kopieren" → "E-Mail-Adresse
            kopiert" for 1.5s after a successful copy. Icon swaps to a checkmark
            with a subtle 0.95 → 1.0 scale bounce. */}
        <button
          type="button"
          onClick={handleCopy}
          className="group flex items-center gap-1.5 mt-0.5 max-w-full w-full text-left active:opacity-80 transition-opacity cursor-pointer"
          title={copied ? "E-Mail-Adresse kopiert" : "Kopieren"}
          aria-label={copied ? "E-Mail-Adresse kopiert" : "Kopieren"}
          data-testid="inbox-copy-address"
        >
          <span className="text-[11px] text-[#888888] group-hover:text-[#555555] transition-colors truncate">
            {alias.alias_address}
          </span>
          <span
            className={`inline-flex transition-transform duration-200 ease-out shrink-0 ${
              copied ? "scale-100" : "scale-95 group-hover:scale-100 group-active:scale-90"
            }`}
            aria-hidden
          >
            {copied
              ? <Check size={11} className="text-emerald-500" />
              : <Copy size={11} className="text-[#888888] group-hover:text-[#555555]" />
            }
          </span>
        </button>
      </div>
      <button
        type="button"
        onClick={() => setCardOpen(true)}
        className="shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-medium text-[#555555] hover:text-[#111111] hover:bg-[#F5F5F5] border border-[#E5E5E5] transition-colors"
        data-testid="inbox-show-card"
        aria-label="Show alias display card"
      >
        <IdCard size={12} strokeWidth={1.75} />
        Show Card
      </button>
      <AliasCardDialog
        open={cardOpen}
        onOpenChange={setCardOpen}
        alias={alias}
      />
    </div>
  );
}

/* Clean email row with folder-aware 3-dot menu */
function EmailRow({ email, isSelected, isNew, onClick, onAction, categories, labels, showAliasBadge, folder, isChecked, onToggleCheck, activeFilters, onBadgeFilter }) {
  const isUnread = email.status === "unread";
  const timeStr = email.created_at
    ? formatDistanceToNow(parseUTC(email.created_at), { addSuffix: true })
    : "";
  const isArchiveFolder = folder === "archive";
  const isSpamFolder = folder === "spam";
  const isSentFolder = folder === "sent";

  // Resolve category/label names for badge display
  const emailCatIds = email.category_ids || [];
  const emailLblIds = email.label_ids || [];
  const catBadges = emailCatIds.map(id => categories.find(c => c.category_id === id)).filter(Boolean);
  const lblBadges = emailLblIds.map(id => labels.find(l => l.label_id === id)).filter(Boolean);
  const allBadges = [
    ...catBadges.map(c => ({ type: "category", id: c.category_id, name: c.name, color: c.color })),
    ...lblBadges.map(l => ({ type: "label", id: l.label_id, name: l.name })),
  ];

  // Determine which badges are currently the sole active filter for their bucket.
  const activeCatIds = activeFilters?.categoryIds || [];
  const activeLblIds = activeFilters?.labelIds || [];
  const isBadgeActive = (b) =>
    (b.type === "category" && activeCatIds.length === 1 && activeCatIds[0] === b.id) ||
    (b.type === "label" && activeLblIds.length === 1 && activeLblIds[0] === b.id);

  const truncName = (name) => name.length > 8 ? name.slice(0, 8) + "…" : name;

  return (
    <div
      className={`border-b border-[#E5E5E5] cursor-pointer transition-colors hover:bg-[#F5F5F5] group/row ${isSelected ? "bg-violet-50" : ""} ${isNew ? "bg-violet-50/50" : ""} ${isChecked ? "bg-[#F5F5F5]/60" : ""}`}
      onClick={onClick}
      data-testid={`email-item-${email.email_id}`}
    >
      <div className="flex items-start gap-2 px-4 py-3 min-h-[72px]">
        {/* Checkbox — always visible on mobile/tablet (no hover state on
            touch), reveal-on-hover on desktop per original design. */}
        <div className="pt-1.5 w-5 shrink-0 flex justify-center">
          <button
            onClick={(e) => { e.stopPropagation(); onToggleCheck(email.email_id); }}
            className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${
              isChecked
                ? "bg-zinc-900 border-zinc-900"
                : "border-zinc-300 xl:opacity-0 xl:group-hover/row:opacity-100"
            }`}
            data-testid={`checkbox-${email.email_id}`}
            aria-label={isChecked ? "Deselect email" : "Select email"}
          >
            {isChecked && <Check size={10} className="text-white" strokeWidth={3} />}
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <span className={`text-sm truncate flex items-center gap-1.5 ${isUnread ? "font-semibold text-[#111111]" : "font-medium text-[#555555]"}`}>
              {isUnread && <span className="w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0" data-testid={`unread-dot-${email.email_id}`} />}
              {email.direction === "outbound"
                ? <span className="flex items-center gap-1"><Send size={12} className="text-[#888888] shrink-0" /> To: {email.to_email}</span>
                : email.sender_name
              }
            </span>
            <span className="text-[11px] text-[#888888] shrink-0 font-mono">{timeStr}</span>
          </div>
          <p className={`text-sm truncate mt-0.5 ${isUnread ? "text-[#111111]" : "text-[#555555]"}`}>
            {email.subject}
          </p>
          <div className="flex items-center gap-2 mt-1">
            <p className="text-xs text-[#888888] truncate flex-1">{email.body_preview}</p>
            {email.has_attachments && <Paperclip size={12} className="text-[#888888] shrink-0" />}
          </div>
          {/* Category + Label badges — clickable: replace-filter (PROMPT 007).
              Stops propagation so clicking a badge does NOT open the email. */}
          {allBadges.length > 0 && (
            <div className="flex items-center gap-1 mt-1.5 flex-wrap" data-testid={`badges-${email.email_id}`}>
              {allBadges.map((b, i) => {
                const active = isBadgeActive(b);
                const baseCls = b.type === "category"
                  ? "font-medium text-white"
                  : "text-[#555555] bg-[#F5F5F5] border border-[#E5E5E5]";
                const ringCls = active ? " ring-1 ring-offset-1 ring-zinc-900" : "";
                return (
                  <button
                    key={`${b.type}-${b.id}-${i}`}
                    type="button"
                    title={active ? `Remove "${b.name}" filter` : `Filter by "${b.name}"`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onBadgeFilter) onBadgeFilter(b.type, b.id);
                    }}
                    className={`inline-flex items-center text-[10px] px-1.5 py-0.5 rounded cursor-pointer hover:opacity-80 transition-opacity ${baseCls}${ringCls}`}
                    style={b.type === "category" ? { backgroundColor: b.color } : undefined}
                    data-testid={`badge-${b.type}-${b.id}-${email.email_id}`}
                  >
                    {truncName(b.name)}
                  </button>
                );
              })}
            </div>
          )}
          {showAliasBadge && (email.alias_address || email.alias_label) && (
            <span className="inline-block mt-1.5 text-[10px] tracking-wide text-[#888888] bg-white px-1.5 py-0.5 rounded border border-[#E5E5E5]" data-testid={`alias-badge-${email.email_id}`}>
              {email.alias_label ? `${email.alias_label} (${email.alias_address || ""})` : email.alias_address}
            </span>
          )}
        </div>

        {/* Star toggle */}
        <div className="flex items-center shrink-0 pt-1" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => onAction(email.email_id, email.is_vip ? "unvip" : "vip")}
            className="p-1 rounded hover:bg-amber-50 transition-colors"
            data-testid={`star-toggle-${email.email_id}`}
          >
            <Star size={16} className={email.is_vip ? "text-amber-600 fill-amber-400" : "text-zinc-300 hover:text-[#888888]"} />
          </button>
        </div>

        {/* 3-dot action menu — always visible on desktop */}
        <div className="hidden xl:flex items-center shrink-0 pt-1" onClick={(e) => e.stopPropagation()}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="p-2 -m-0.5 rounded-md hover:bg-[#F5F5F5] text-[#888888] hover:text-[#555555] transition-colors" data-testid={`more-btn-${email.email_id}`}>
                <MoreVertical size={16} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              {/* Unified Move to… items — only valid destinations for current folder */}
              {!isSentFolder && (
                <MoveToMenu
                  folder={folder}
                  onAction={(action) => onAction(email.email_id, action)}
                  variant="menuitem"
                  testIdPrefix={`row-move-to-${email.email_id}`}
                />
              )}
              <DropdownMenuItem onClick={() => onAction(email.email_id, "delete")} className="text-red-600 focus:text-red-600" data-testid={`delete-btn-${email.email_id}`}>
                <Trash2 size={14} className="mr-2" /> Delete
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {isUnread ? (
                <DropdownMenuItem onClick={() => onAction(email.email_id, "read")} data-testid={`read-btn-${email.email_id}`}>
                  <MailOpen size={14} className="mr-2" /> Mark as read
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem onClick={() => onAction(email.email_id, "unread")} data-testid={`unread-btn-${email.email_id}`}>
                  <Mail size={14} className="mr-2" /> Mark as unread
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
}
